import React, { useState } from 'react';
import { Skeleton, Tooltip } from 'antd';
import MobileIcon from '@assets/svgs/blue-mobile-icon.svg';
import EmailIcon from '@assets/svgs/blue-email-icon.svg';
import CommentIcon from '@assets/svgs/chat-balloon-icon.svg';
import LocationIcon from '@assets/svgs/location-icon.svg';
import ClockIcon from '@assets/svgs/clock-icon.svg';
import RedDot from '@assets/svgs/red-dot-icon.svg';
import PinIcon from '@assets/svgs/purple-pin-icon.svg';
import UiTag from '@components/ui/tag/UiTag';
import StateEnum, { StateEnumTag } from '@constants/state.enum';
import { StepApplicantElement } from '@models/applicants';
import Applicant from '@components/app-components/applicant/Applicant';
import { useRouter } from 'next/router';
import getApplicantComments from '@services/comments/comments-http.service';
import useNotification from '@hooks/notification/useNotification';
import { ApplicantCommentsModel } from '@models/comments';
import { dateFromNow } from '@services/date/date-from-now';
import styles from './HiredApplicantCard.module.scss';
import { useTranslation } from 'next-i18next';

export type PositionCardProps = {
  applicant: StepApplicantElement;
  applicants: Array<StepApplicantElement>;
  fetchApplicants: () => void;
};

const HiredApplicantCard: React.FC<PositionCardProps> = ({ applicants, applicant, fetchApplicants }) => {
  const router = useRouter();
  const { t: translate } = useTranslation('applicantCard');
  const [commentsLoading, setCommentsLoading] = useState<boolean>(false);
  const [selectedApplicant, setSelectedApplicant] = useState<number>();
  const [comments, setComments] = useState<ApplicantCommentsModel>({ count: 0, items: [] });
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const copyToClipboard = (email: string, e: any) => {
    const textField = document.createElement('textarea');
    textField.innerText = email;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand('copy');
    textField.remove();
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = (event: any) => {
    setSelectedApplicant(event.currentTarget.id);
    setIsDrawerVisible(true);
  };

  const fetchApplicantComments = async () => {
    try {
      setCommentsLoading(true);
      const commentsElem: ApplicantCommentsModel = await getApplicantComments(applicant.id.toString());
      setComments(commentsElem);
      setCommentsLoading(false);
    } catch (e: any) {
      generalError();
      setCommentsLoading(false);
    }
  };

  const renderComments = () => {
    if (commentsLoading) {
      return <Skeleton active />;
    }
    return (
      <div className={styles.wrapCommentsTooltip}>
        <div className={`h6 ${styles.titleComments}`}>{translate('comments')}</div>
        <div className={styles.wrapComments}>
          {comments.count > 0 ? (
            comments.items.map((comment) => {
              return (
                <div key={comment.id} className={styles.wrapTooltipComment}>
                  <div
                    className={`h6 ${styles.userComment}`}
                  >{`${comment.user.firstname} ${comment.user.lastname}`}</div>
                  <div className={`main-body-text ${styles.commentText}`}>{comment.comment}</div>
                </div>
              );
            })
          ) : (
            <div className={styles.wrapTooltipComment}>
              <div className={`h6 ${styles.userComment}`}>{translate('no_comments')}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapPreviewCard}>
      <div
        id={applicant.id.toString()}
        className={styles.wrapPositionCard}
        onClick={handleClick}
        role="button"
        onKeyDown={handleClick}
        tabIndex={0}
        data-testid="applicant-card"
      >
        {!applicant.viewed && (
          <Tooltip
            placement="top"
            mouseLeaveDelay={0.1}
            color="#3e4b5e"
            title={<div className={`small-body-text ${styles.redDotTooltip}`}>Δεν έχει προβληθεί</div>}
            trigger="hover"
          >
            <RedDot className={styles.redDotIcon} />
          </Tooltip>
        )}
        <div className={styles.positionCard}>
          <div className={styles.wrapCardHeader}>
            <div className={`h5 ${styles.nameText}`}>
              {applicant.firstName} {applicant.lastName}
            </div>
            <div className={styles.wrapEditDots} />
          </div>
          {applicant.location && (
            <div className={styles.locationTag}>
              <UiTag text={applicant.location} type={StateEnumTag.DEFAULT} icon={<LocationIcon />} />
            </div>
          )}
          <div className={styles.wrapPhoneEmail}>
            {applicant.phoneNumber && (
              <div
                className={styles.wrapElementTag}
                onClick={(e) => {
                  router.push(`tel:${applicant.phoneNumber}`);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  router.push(`tel:${applicant.phoneNumber}`);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <div className={styles.elementTag}>
                  <MobileIcon />
                  <div className="h6">{applicant.phoneNumber}</div>
                </div>
              </div>
            )}
            {applicant.email && (
              <Tooltip
                placement="topLeft"
                mouseLeaveDelay={2}
                color="#3e4b5e"
                title={<div className="small-body-text">Email copied successfully!</div>}
                trigger="click"
              >
                <div
                  className={styles.wrapElementTag}
                  onClick={(e) => copyToClipboard(applicant.email, e)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => copyToClipboard(applicant.email, e)}
                  data-testid="email-copy-button"
                >
                  <div className={styles.elementTag}>
                    <EmailIcon />
                    <div className="h6">{applicant.email}</div>
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        <div className={styles.wrapCardFooter}>
          <div className={styles.leftFooter}>
            <div className={`small-caps-regular ${styles.dateBefore}`}>
              <ClockIcon className={styles.clockIcon} />
              {dateFromNow(applicant.createdDate)}
            </div>
            {applicant.sourced && (
              <Tooltip
                placement="top"
                mouseLeaveDelay={0.1}
                color="#3e4b5e"
                title={<div className="small-body-text">Sourced</div>}
                trigger="hover"
              >
                <div className={styles.pinContainer}>
                  <PinIcon className={styles.pinIcon} data-testid="pin-icon" />
                </div>
              </Tooltip>
            )}
          </div>
          <Tooltip
            placement="bottom"
            mouseLeaveDelay={0.1}
            color="#111725"
            title={renderComments()}
            trigger="click"
            overlayClassName="commentsTooltip"
            showArrow
          >
            <div
              role="button"
              className={styles.wrapCardComments}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await fetchApplicantComments();
              }}
              tabIndex={0}
              onKeyDown={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await fetchApplicantComments();
              }}
              data-testid="comments-tooltip"
            >
              <CommentIcon />
              <div
                className={`small-caps-regular ${
                  applicant.commentsCounter > 0 ? styles.commentsNumber : styles.noCommentsNumber
                }`}
              >
                {applicant.commentsCounter}
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
      {selectedApplicant && (
        <Applicant
          key={selectedApplicant}
          applicants={applicants}
          setVisible={setIsDrawerVisible}
          visible={isDrawerVisible}
          applicantId={selectedApplicant}
          fetchApplicants={() => fetchApplicants()}
          arrows
        />
      )}
    </div>
  );
};

export default HiredApplicantCard;
